"use client";

import { useContext, useMemo } from "react";
import {
  LuUserMinus as LuUserMinus2,
  LuUserPlus as LuUserPlus2,
} from "react-icons/lu";

import { setDialog } from "@/components/app/dialogs/dialog-provider";
import { DesktopTooltip } from "@/components/shared/desktop-tooltip";
import { Button } from "@/components/ui/button";
import { UserContext } from "@/contexts/user-context";
import { useUserAssociatedLists } from "@/hooks/use-user-associated-lists";
import { useUserFollowStatus } from "@/hooks-api/use-user-follow-status";
import { DIALOG } from "@/utils/dialog-routes";
import { cn } from "@/utils/lib";

type FollowUserListsButtonProps = {
  userLabel: string;
  userPublicKey: string;
  className?: string;
  variant?: "post" | "menu" | "inline" | "profile";
};

const inactiveButtonClassName = `
  shrink-0 gap-2 rounded-full border-border-light bg-transparent
  text-foreground hover:border-border hover:bg-transparent hover:text-foreground
`;

const iconButtonClassName = "size-10 px-0";

export const FollowUserListsButton = ({
  userLabel,
  userPublicKey,
  className,
  variant = "post",
}: FollowUserListsButtonProps) => {
  const isMenuVariant = variant === "menu";
  const isInlineVariant = variant === "inline";
  const isProfileVariant = variant === "profile";
  const { currentUserPublicKey } = useContext(UserContext);
  const { isUserFollowedByPublicKey } = useUserFollowStatus(userPublicKey);
  const { data: associatedLists = [] } = useUserAssociatedLists({
    ownerPublicKey: currentUserPublicKey,
    userPublicKey,
    enabled: !!currentUserPublicKey,
  });

  const isActive = useMemo(() => {
    return isUserFollowedByPublicKey || associatedLists.length > 0;
  }, [associatedLists.length, isUserFollowedByPublicKey]);
  const ActionIcon = isActive ? LuUserMinus2 : LuUserPlus2;
  const openDialog = () => {
    setDialog(DIALOG.FollowUserLists, {
      open: true,
      params: {
        userLabel,
        userPublicKey,
      },
    });
  };

  if (isMenuVariant) {
    return (
      <Button
        variant="ghost"
        className={cn(
          `
            h-auto w-full justify-start gap-2 p-0 text-sm text-foreground
            hover:bg-transparent hover:text-primary
          `,
          className,
        )}
        onClick={openDialog}
      >
        <ActionIcon className="size-4 shrink-0" />
        <span>{isActive ? "Unfollow" : "Follow"}</span>
      </Button>
    );
  }

  if (isInlineVariant) {
    return (
      <Button
        variant={isActive ? "default" : "outline"}
        className={cn(
          "h-10 shrink-0 gap-2 rounded-full px-3",
          !isActive && inactiveButtonClassName,
          className,
        )}
        onClick={openDialog}
      >
        <ActionIcon className="size-4 shrink-0" />
        <span className="font-medium">{isActive ? "Unfollow" : "Follow"}</span>
      </Button>
    );
  }

  if (isProfileVariant) {
    const profileFullButton = (
      <Button
        variant="outline"
        className={cn(
          inactiveButtonClassName,
          "hidden h-10 px-3 sm:inline-flex",
          className,
        )}
        onClick={openDialog}
      >
        <LuUserPlus2 className="size-4 shrink-0" />
        <span className="font-medium">Follow</span>
      </Button>
    );

    const profileIconButton = (
      <Button
        variant={isActive ? "default" : "outline"}
        className={cn(
          "size-10 shrink-0 px-0",
          !isActive && inactiveButtonClassName,
          {
            "inline-flex": isActive,
            "inline-flex sm:hidden": !isActive,
          },
          className,
        )}
        onClick={openDialog}
      >
        <ActionIcon className="size-4 shrink-0" />
        <span className="sr-only">{isActive ? "Unfollow" : "Follow"}</span>
      </Button>
    );

    return (
      <div className="contents">
        {!isActive && profileFullButton}
        <DesktopTooltip content={isActive ? "Unfollow" : "Follow"}>
          {profileIconButton}
        </DesktopTooltip>
      </div>
    );
  }

  const fullButton = (
    <Button
      variant="outline"
      className={cn(
        inactiveButtonClassName,
        "hidden h-10 px-3 @min-[560px]:inline-flex",
        className,
      )}
      onClick={openDialog}
    >
      <LuUserPlus2 className="size-4 shrink-0" />
      <span className="font-medium">Follow</span>
    </Button>
  );

  const compactButton = (
    <Button
      variant={isActive ? "default" : "outline"}
      className={cn(
        iconButtonClassName,
        !isActive && inactiveButtonClassName,
        {
          "inline-flex": isActive,
          "inline-flex @min-[560px]:hidden": !isActive,
        },
        className,
      )}
      onClick={openDialog}
    >
      <ActionIcon className="size-4 shrink-0" />
      <span className="sr-only">{isActive ? "Unfollow" : "Follow"}</span>
    </Button>
  );

  return (
    <div className="contents">
      {!isActive && fullButton}
      <DesktopTooltip content={isActive ? "Unfollow" : "Follow"}>
        {compactButton}
      </DesktopTooltip>
    </div>
  );
};
